import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import { TrackingEvents, isValidPersonalNumberFormat } from '@estercare/ester-shared'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { usePersonalNumber } from '../../../hooks/usePersonalNumber'
import { useSelectUserBirthYear } from '../../../state/selectors'
import { theme } from '../../../theme/theme'
import { formatPersonalNumber } from '../../../utils/personalNumber'
import { queryErrorHandlerWithMessage } from '../../../utils/queryErrorHandler'
import { JournalConsentCheckbox, useScreeningSummaryOptIn } from '../../consent/JournalConsent'
import { TextInput } from '../../inputs/Text'

export const useScreeningContinue = (analyticsAction: string) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const emitEvent = useEmitEvent()
  const birthYear = useSelectUserBirthYear()
  const { savePersonalNumber } = usePersonalNumber()
  const summaryOptIn = useScreeningSummaryOptIn()
  const [personalNumber, setPersonalNumber] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { askForPersonalNumber } = summaryOptIn
  const isPersonalNumberValid = isValidPersonalNumberFormat(personalNumber)

  const onContinue = async () => {
    emitEvent({ eventType: TrackingEvents.CLICK, action: analyticsAction })

    try {
      await summaryOptIn.saveOptIn()
    } catch (saveError) {
      setError(queryErrorHandlerWithMessage('save screening summary consent', t)(saveError as Error))
      return
    }

    if (askForPersonalNumber) {
      try {
        await savePersonalNumber({ personalNumber, isValid: isPersonalNumberValid, setError, t })
      } catch {
        return
      }
    }

    navigate('/portal/health-services/screening/questions')
  }

  const optInFields =
    summaryOptIn.showOptIn || askForPersonalNumber ? (
      <Stack textAlign="left" gap={`${theme.spacing.mediumLarge}px`}>
        {summaryOptIn.showOptIn && (
          <JournalConsentCheckbox
            title={t('employee.screening.journalTitle')}
            text={t('employee.screening.journalText')}
            label={t('employee.screening.journalCheckbox')}
            checked={summaryOptIn.checked}
            onChange={summaryOptIn.setChecked}
          />
        )}
        {askForPersonalNumber && (
          <TextInput
            label={t('common.form.personalNumber')}
            onChange={(e) => setPersonalNumber(formatPersonalNumber(e.target.value))}
            value={personalNumber}
            placeholder={birthYear ? `${birthYear}XXXX-XXXX` : '19XXXXXX-XXXX'}
            borderColor={isPersonalNumberValid ? theme.color.green : undefined}
          />
        )}
      </Stack>
    ) : undefined

  return {
    onContinue,
    optInFields,
    isButtonDisabled: askForPersonalNumber && !isPersonalNumberValid,
    errorMsg: error || undefined,
  }
}
